import "./App.css";
import { HelmetProvider } from "react-helmet-async";
import { ConfigProvider, theme as antdThemeAlgos } from "antd";
import type { ThemeConfig } from "antd";
import { ToastsProvider } from "./contexts/toasts";
import { AuthProvider } from "./contexts/auth";
import { ClipboardProvider } from "./contexts/clipboard";
import { UploadIndicatorProvider } from "./contexts/uploadIndicator";
import { ThemeModeProvider, useThemeMode } from "./contexts/themeMode";
import { RealtimeProvider } from "./contexts/realtime";
import AppRouter from "./router/AppRouter";

// Brand theme applied portal-wide so every AntD surface (buttons, inputs,
// selects, tables, tabs, modals, pagination, switches…) inherits the oxblood
// identity + softer radius from DESIGN.md instead of the default AntD blue.
const baseTheme: ThemeConfig = {
  token: {
    colorPrimary: "#9c1419",
    colorLink: "#c0282d",
    colorLinkHover: "#d6474d",
    colorLinkActive: "#9c1419",
    colorInfo: "#9c1419",
    borderRadius: 10,
    fontFamily:
      "system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
  },
  components: {
    Button: { fontWeight: 600, primaryShadow: "0 2px 6px rgba(156,20,25,0.24)" },
    Table: {
      headerColor: "#94a3b8",
      rowHoverBg: "rgba(156,20,25,0.12)",
      cellPaddingBlock: 12,
      borderRadius: 12,
    },
    Modal: { borderRadiusLG: 16 },
    Card: { borderRadiusLG: 14 },
    Input: { activeShadow: "0 0 0 3px rgba(156,20,25,0.18)" },
    InputNumber: { activeShadow: "0 0 0 3px rgba(156,20,25,0.18)" },
    Segmented: { itemSelectedBg: "#9c1419", itemSelectedColor: "#ffffff" },
  },
};

// Dark surfaces are repainted onto the neutral-graphite "arena" family (the
// sidebar palette) instead of AntD's default near-black, so every AntD panel,
// table, modal and dropdown matches the shell. Only applied under the dark
// algorithm (light mode keeps AntD's derived surfaces).
const darkSurfaceTokens: ThemeConfig["token"] = {
  colorBgBase: "#0c0e13",
  colorBgLayout: "#0c0e13",
  colorBgContainer: "#171a22",
  colorBgElevated: "#1c202a",
  colorBorder: "#2a2f3b",
  colorBorderSecondary: "#20242e",
};

const darkTableTokens = {
  headerBg: "#1c202a",
  headerColor: "#cbd2dd",
  colorBgContainer: "#171a22",
  rowHoverBg: "rgba(192,40,45,0.16)",
  borderColor: "#262b36",
};

function App() {
  return <AppRouter />;
}

// Reads the global theme mode and feeds AntD the matching algorithm (light /
// dark) while keeping the brand tokens.
function ThemedApp() {
  const { mode } = useThemeMode();
  const isDark = mode === "dark";
  return (
    <ConfigProvider
      theme={{
        ...baseTheme,
        token: {
          ...baseTheme.token,
          ...(isDark ? darkSurfaceTokens : {}),
        },
        components: {
          ...baseTheme.components,
          ...(isDark
            ? {
                Table: { ...baseTheme.components?.Table, ...darkTableTokens },
                Layout: { bodyBg: "transparent", headerBg: "#9c1419" },
              }
            : {}),
        },
        algorithm: isDark
          ? antdThemeAlgos.darkAlgorithm
          : antdThemeAlgos.defaultAlgorithm,
      }}
    >
      <App />
    </ConfigProvider>
  );
}

export default function withContext() {
  return (
    <HelmetProvider>
      <ThemeModeProvider>
        <ToastsProvider>
          <AuthProvider>
            <RealtimeProvider>
              <ClipboardProvider>
                <UploadIndicatorProvider>
                  <ThemedApp />
                </UploadIndicatorProvider>
              </ClipboardProvider>
            </RealtimeProvider>
          </AuthProvider>
        </ToastsProvider>
      </ThemeModeProvider>
    </HelmetProvider>
  );
}
